import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';
import { AttentionStrip } from './AttentionStrip.js';
import { HostMoney } from './HostMoney.js';
import { MonthPanel } from './MonthPanel.js';
import { OverviewMonth } from './OverviewMonth.js';
import { NextCard, TodayCard, TodayPanel } from './TodayPanel.js';
import { TripsCard } from './TripsCard.js';
import { monthName } from './dashboardSeries.js';
import type { MonthSummary, MonthTrips, TodaySummary } from './types.js';

/**
 * 355: the first screen staff open each morning (design 2a). Today, what needs action, down the left; This month, the
 * money and fleet picture, on the right; stacked on a phone, Today first.
 *
 * The two halves are separate reads (FR-022): one failing never blanks the other. Each re-reads every minute while
 * the page is visible and when the tab comes back (FR-023). A refusal and a failure are different facts and never
 * share a sentence, and neither shows zeros (062 FR-010).
 */
export const DASHBOARD_REFRESH_MS = 60_000;

const statusOf = (error: unknown) => (error as { status?: number } | null)?.status;
const isRefusal = (error: unknown) => statusOf(error) === 401 || statusOf(error) === 403;
/** 422 (#1526, FR-008a): the month's money is a SuperAdmin's; an Admin's refusal is a layout, not an error. */
const isMoneyRefusal = (error: unknown) =>
  statusOf(error) === 403 &&
  (error as { code?: string } | null)?.code === 'DASHBOARD_MONEY_FORBIDDEN';

function useHalf<T>(half: 'today' | 'month') {
  return useQuery({
    queryKey: ['dashboard', half],
    queryFn: () => api.get<T>(`/dashboard/${half}`),
    // A refusal is not a transient fault; a failure is offered a manual retry instead.
    retry: false,
    refetchInterval: DASHBOARD_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

function HalfError({ id, what, onRetry }: { id: string; what: string; onRetry: () => void }) {
  return (
    <Card data-testid={id}>
      <p className="text-sm text-steel">{what} could not be loaded. Try again shortly.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg border border-hairline-strong px-3 py-1.5 text-sm font-medium text-charcoal hover:bg-surface"
      >
        Try again
      </button>
    </Card>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-16">
      <Spinner size="lg" />
    </div>
  );
}

export function DashboardPage() {
  const queryClient = useQueryClient();
  const today = useHalf<TodaySummary>('today');
  const month = useHalf<MonthSummary>('month');

  const refused = isRefusal(today.error) && isRefusal(month.error);
  const updatedAt = Math.max(today.dataUpdatedAt, month.dataUpdatedAt);
  const asOf = today.data?.as_of ?? (updatedAt ? new Date(updatedAt).toISOString() : null);
  const supplier = (today.data?.scope ?? month.data?.scope) === 'supplier';
  const moneyRefused = isMoneyRefusal(month.error);
  const overview = month.data?.overview;
  // 422 v2 (design approved 2026-10-27): the platform page is two sections, Today then the month; a host keeps 355's.
  const platform =
    today.data?.scope === 'platform' || month.data?.scope === 'platform' || moneyRefused;
  // An Admin is refused the month's money but keeps its Trips card, read on its own (no money in it).
  const trips = useQuery({
    queryKey: ['dashboard', 'trips'],
    queryFn: () => api.get<MonthTrips>('/dashboard/trips'),
    enabled: moneyRefused,
    retry: false,
    refetchInterval: DASHBOARD_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  // 355: a host's Today column (the platform's Today section is laid out below).
  const todayContent = today.data ? (
    <TodayPanel data={today.data} />
  ) : today.isError ? (
    <HalfError id="today-error" what="Today" onRetry={() => today.refetch()} />
  ) : (
    <Loading />
  );

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={
          asOf ? (
            <span data-testid="dashboard-day">
              {mytDayLabel(asOf)} · Malaysia time{supplier ? ' · your organisation’s cars' : ''}
            </span>
          ) : undefined
        }
        actions={
          refused ? undefined : (
            <>
              {updatedAt ? (
                <span
                  data-testid="dashboard-updated"
                  className="hidden text-sm text-steel sm:inline"
                >
                  Updated {mytClock(new Date(updatedAt))}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['dashboard'] })}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline-strong px-3.5 text-sm font-medium text-charcoal hover:bg-surface"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Refresh
              </button>
            </>
          )
        }
      />

      {refused ? (
        <Card>
          <p className="text-sm text-steel">
            Platform statistics are not available for your account. Use the sections in the sidebar
            to manage your own fleet, pricing and bookings.
          </p>
        </Card>
      ) : platform ? (
        <div className="flex flex-col gap-6">
          <section aria-label="Today" className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[13px] font-bold tracking-wider text-ink uppercase">Today</h2>
              <Link
                to="/schedule"
                className="text-sm font-medium text-primary hover:underline dark:text-brand-blue-mid"
              >
                Schedule →
              </Link>
            </div>
            {today.data?.queues ? (
              <AttentionStrip
                queues={today.data.queues}
                asOf={today.data.as_of}
                className={moneyRefused ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : undefined}
              />
            ) : null}
            {today.data ? (
              <div className="grid items-start gap-4 lg:grid-cols-2">
                <section data-testid="dashboard-today" aria-label="Today counts">
                  <TodayCard data={today.data} columns={moneyRefused ? 1 : 2} />
                </section>
                <NextCard data={today.data} raised />
              </div>
            ) : today.isError ? (
              <HalfError id="today-error" what="Today" onRetry={() => today.refetch()} />
            ) : (
              <Loading />
            )}
          </section>
          <hr className="border-hairline dark:border-hairline-strong" />
          <section aria-label="This month" data-testid="dashboard-month" className="min-w-0">
            {overview && month.data ? (
              <OverviewMonth
                data={month.data}
                overview={overview}
                today={today.data?.day ?? month.data.month}
              />
            ) : moneyRefused ? (
              trips.data ? (
                <div className="flex flex-col gap-4">
                  <h2 className="text-[13px] font-bold tracking-wider text-ink uppercase">
                    {monthName(trips.data.month).split(' ')[0]}
                  </h2>
                  <TripsCard trips={trips.data.trips} month={trips.data.month} wide />
                </div>
              ) : trips.isError ? (
                <HalfError
                  id="trips-error"
                  what="Trips this month"
                  onRetry={() => trips.refetch()}
                />
              ) : (
                <Loading />
              )
            ) : month.data ? (
              // A month with no overview (a backend older than 422): the 355 panel, never an endless spinner.
              <MonthPanel data={month.data} today={today.data?.day ?? month.data.month} />
            ) : month.isError ? (
              <HalfError id="month-error" what="This month" onRetry={() => month.refetch()} />
            ) : (
              <Loading />
            )}
          </section>
        </div>
      ) : (
        // 424 (mock-up approved 2026-10-27): a host's page is Today, then "Your money" (its own read), then This month.
        <div data-testid="dashboard-grid" className="flex flex-col gap-6">
          <section data-testid="dashboard-today" aria-label="Today">
            {todayContent}
          </section>
          {supplier ? (
            <HostMoney currentMonth={(today.data?.day ?? month.data?.month ?? '').slice(0, 7)} />
          ) : null}
          <section data-testid="dashboard-month" aria-label="This month" className="min-w-0">
            {month.data ? (
              <MonthPanel data={month.data} today={today.data?.day ?? month.data.month} />
            ) : month.isError ? (
              <HalfError id="month-error" what="This month" onRetry={() => month.refetch()} />
            ) : (
              <Loading />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
